import { useEffect, useRef, useState } from 'react';
import grapesjs, { Editor } from 'grapesjs';
import 'grapesjs/dist/css/grapes.min.css';
import { conteudoService } from '../../services/conteudoService';
import './AdminConteudo.css';

const PAGES = [
  { slug: 'landing-page', label: 'Landing Page' },
  { slug: 'privacidade', label: 'Privacidade' },
  { slug: 'cookies', label: 'Cookies' },
  { slug: 'termos', label: 'Termos de Uso' },
  { slug: 'lgpd', label: 'LGPD' },
];

function AdminConteudo() {
  const editorRef = useRef<HTMLDivElement>(null);
  const gjsInstance = useRef<Editor | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentSlug, setCurrentSlug] = useState('landing-page');

  const loadPage = async (slug: string) => {
    setLoading(true);
    setMessage('');

    // Destroy existing editor
    if (gjsInstance.current) {
      gjsInstance.current.destroy();
      gjsInstance.current = null;
    }

    try {
      const data = await conteudoService.buscarEditor(slug);

      const editor = grapesjs.init({
        container: editorRef.current!,
        height: 'calc(100vh - 110px)',
        width: 'auto',
        storageManager: false,
        plugins: [],
        canvas: {
          styles: [
            'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap',
            'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&display=swap',
          ],
        },
        deviceManager: {
          devices: [
            { name: 'Desktop', width: '' },
            { name: 'Tablet', width: '768px', widthMedia: '992px' },
            { name: 'Mobile', width: '375px', widthMedia: '480px' },
          ],
        },
      });

      // Load saved project data if exists
      if (data.gjs_data && Object.keys(data.gjs_data).length > 0) {
        editor.loadProjectData(data.gjs_data as ReturnType<Editor['getProjectData']>);
      } else if (data.html) {
        editor.setComponents(data.html);
        editor.setStyle(data.css || '');
      }

      gjsInstance.current = editor;
    } catch {
      setMessage('Erro ao carregar o editor.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPage(currentSlug);

    return () => {
      if (gjsInstance.current) {
        gjsInstance.current.destroy();
      }
    };
  }, []);

  const handlePageChange = (slug: string) => {
    setCurrentSlug(slug);
    loadPage(slug);
  };

  const handleSave = async () => {
    if (!gjsInstance.current) return;

    setSaving(true);
    setMessage('');

    try {
      const editor = gjsInstance.current;
      const html = editor.getHtml();
      const css = editor.getCss() || '';
      const gjsData = editor.getProjectData();

      await conteudoService.salvarPagina(currentSlug, html, css, gjsData);
      setMessage('Página salva com sucesso!');
      setTimeout(() => setMessage(''), 3000);
    } catch {
      setMessage('Erro ao salvar. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const currentLabel = PAGES.find((p) => p.slug === currentSlug)?.label || currentSlug;

  return (
    <div className="admin-conteudo">
      <div className="admin-conteudo-toolbar">
        <div className="admin-conteudo-toolbar__left">
          <h2>Editor — {currentLabel}</h2>
          <select
            className="admin-conteudo-toolbar__select"
            value={currentSlug}
            onChange={(e) => handlePageChange(e.target.value)}
          >
            {PAGES.map((page) => (
              <option key={page.slug} value={page.slug}>
                {page.label}
              </option>
            ))}
          </select>
        </div>
        <div className="admin-conteudo-toolbar__actions">
          {message && (
            <span
              className={`admin-conteudo-toolbar__message ${message.includes('Erro') ? 'admin-conteudo-toolbar__message--error' : ''}`}
            >
              {message}
            </span>
          )}
          <button
            className="admin-conteudo-toolbar__save"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Salvando...' : 'Publicar'}
          </button>
        </div>
      </div>
      {loading && <div className="admin-conteudo-loading">Carregando editor...</div>}
      <div ref={editorRef} className="admin-conteudo-editor" />
    </div>
  );
}

export default AdminConteudo;
